/**
 * Admin > Algo Execution Monitor (ADMIN-only diagnostic/audit page).
 *
 * For every algo/user-strategy evaluation, shows why a trade did or didn't
 * happen: which conditions passed/failed, any broker/API/system failure,
 * and a derived 13-stage execution pipeline trace (Scheduler Trigger ->
 * ... -> Order Acknowledgement & Fill Status). Read-only -- surfaces
 * AlgoExecutionAudit rows recorded by the runner/live-feed-manager
 * instrumentation; never mutates trading state.
 *
 * Known, deliberate gap: NO_TRADE_INSUFFICIENT_BALANCE is part of the
 * status taxonomy but nothing in the current instrumentation writes it yet
 * (no existing balance-check code path to observe without duplicating
 * broker/balance logic) -- so it will never appear in results today.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import algoExecutionAuditService from "@/services/algoExecutionAuditService";
import algoService from "@/services/algoService";
import userService from "@/services/userService";
import type { Algo, AlgoExecutionAuditDetail, AlgoExecutionAuditListItem, PipelineStage, User } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

const ALL_STATUSES = [
  "TRADE_EXECUTED",
  "NO_TRADE_CONDITION_NOT_MET",
  "NO_TRADE_INSUFFICIENT_BALANCE",
  "NO_TRADE_BROKER_API_ERROR",
  "NO_TRADE_STRATEGY_INACTIVE",
  "SYSTEM_ERROR",
];

type ChipColor = "success" | "warning" | "error" | "default";

const STATUS_COLOR: Record<string, ChipColor> = {
  TRADE_EXECUTED: "success",
  NO_TRADE_CONDITION_NOT_MET: "default",
  NO_TRADE_INSUFFICIENT_BALANCE: "warning",
  NO_TRADE_BROKER_API_ERROR: "error",
  NO_TRADE_STRATEGY_INACTIVE: "default",
  SYSTEM_ERROR: "error",
};

// Mirrors backend app.services.algo_execution_pipeline_stages' STAGE_* keys
// and STAGE_LABELS, in the same canonical order -- keep in sync with that
// module if the pipeline stages ever change.
const STAGE_ORDER = [
  "SCHEDULER_TRIGGER",
  "ALGO_ACTIVATION_CHECK",
  "RUNTIME_HEALTH",
  "STRATEGY_INIT",
  "WS_SUBSCRIPTION",
  "CANDLE_RECEIPT",
  "STRATEGY_EVALUATION",
  "ENTRY_CONDITION_CHECK",
  "SIGNAL_GENERATION",
  "RISK_VALIDATION",
  "ORDER_PAYLOAD",
  "BROKER_TRANSMISSION",
  "ORDER_ACK",
];

const STAGE_LABELS: Record<string, string> = {
  SCHEDULER_TRIGGER: "Scheduler Trigger",
  ALGO_ACTIVATION_CHECK: "Algo Activation Check",
  RUNTIME_HEALTH: "AlgoRunner/Runtime Health",
  STRATEGY_INIT: "Strategy Initialization",
  WS_SUBSCRIPTION: "Market-Data WebSocket Subscription",
  CANDLE_RECEIPT: "Market-Data Candle/Tick Receipt",
  STRATEGY_EVALUATION: "Strategy Evaluation Execution",
  ENTRY_CONDITION_CHECK: "Entry-Condition Criteria Check",
  SIGNAL_GENERATION: "Signal Generation (BUY/SELL)",
  RISK_VALIDATION: "Risk Control & Balance Validation",
  ORDER_PAYLOAD: "Order Payload Creation",
  BROKER_TRANSMISSION: "Dhan Broker API Transmission",
  ORDER_ACK: "Order Acknowledgement & Fill Status",
};

const STAGE_STATUS_COLOR: Record<string, string> = {
  PASS: "#2e7d32",
  FAIL: "#d32f2f",
  SKIPPED: "#bdbdbd",
  // Never shown as healthy: no per-cycle evidence exists for the stage.
  NOT_CHECKED: "#78909c",
  // Never shown as healthy: the stage may or may not have run.
  UNKNOWN: "#ef6c00",
};

const STAGE_STATUS_LABEL: Record<string, string> = {
  NOT_CHECKED: "NOT CHECKED",
};

function StatusChip({ status }: { status: string }) {
  return <Chip label={status} color={STATUS_COLOR[status] ?? "default"} size="small" />;
}

function TerminalFailureTag({
  status,
  terminalFailureStage,
}: {
  status: string;
  terminalFailureStage: string | null;
}) {
  if (status === "TRADE_EXECUTED") {
    return <Chip label="Executed" color="success" size="small" variant="outlined" />;
  }
  if (terminalFailureStage) {
    const label = STAGE_LABELS[terminalFailureStage] ?? terminalFailureStage;
    return <Chip label={`FAILED: ${label}`} color="error" size="small" sx={{ fontWeight: 700 }} />;
  }
  return <Chip label="No entry signal" size="small" variant="outlined" />;
}

function PipelineStepper({ stages, dotSize = 12 }: { stages: PipelineStage[]; dotSize?: number }) {
  const byKey = new Map(stages.map((s) => [s.key, s]));
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {STAGE_ORDER.map((key) => {
        const stage = byKey.get(key);
        const st = stage?.status ?? "SKIPPED";
        const label = STAGE_LABELS[key];
        const tooltipParts = [label, STAGE_STATUS_LABEL[st] ?? st];
        if (stage?.detail) tooltipParts.push(stage.detail);
        if (stage?.timestamp) tooltipParts.push(new Date(stage.timestamp).toLocaleTimeString());
        return (
          <Tooltip key={key} title={tooltipParts.join(" — ")} arrow>
            <Box
              sx={{
                width: dotSize,
                height: dotSize,
                borderRadius: "50%",
                bgcolor: STAGE_STATUS_COLOR[st] ?? STAGE_STATUS_COLOR.SKIPPED,
                border: st === "FAIL" ? "2px solid #b71c1c" : "1px solid rgba(0,0,0,0.12)",
                flexShrink: 0,
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function PipelineStageTable({ stages }: { stages: PipelineStage[] }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Stage</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Timestamp</TableCell>
            <TableCell>Detail</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stages.map((s) => (
            <TableRow key={s.key}>
              <TableCell>{s.label}</TableCell>
              <TableCell>
                <Chip
                  label={STAGE_STATUS_LABEL[s.status] ?? s.status}
                  size="small"
                  sx={{
                    bgcolor: STAGE_STATUS_COLOR[s.status] ?? STAGE_STATUS_COLOR.SKIPPED,
                    color: s.status === "SKIPPED" ? "text.primary" : "#fff",
                    fontWeight: 600,
                  }}
                />
              </TableCell>
              <TableCell>{s.timestamp ? new Date(s.timestamp).toLocaleString() : "—"}</TableCell>
              <TableCell sx={{ maxWidth: 420 }}>{s.detail ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function toIsoOrUndefined(localDateTimeValue: string): string | undefined {
  if (!localDateTimeValue) return undefined;
  const d = new Date(localDateTimeValue);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

const PAGE_SIZE = 50;
export function AlgoExecutionMonitorPage() {
  const [algos, setAlgos] = useState<Algo[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [algoId, setAlgoId] = useState("");
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [search, setSearch] = useState("");
  const [failedStage, setFailedStage] = useState("");
  const [failuresOnly, setFailuresOnly] = useState(false);
  // Broker isn't its own server-side filter param on the list endpoint (the
  // backend filters by broker_account_id, not a human-friendly broker
  // name) -- so this narrows the already-fetched page client-side,
  // populated from whatever broker names actually appear in the results.
  const [brokerFilter, setBrokerFilter] = useState("");

  const [items, setItems] = useState<AlgoExecutionAuditListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [detail, setDetail] = useState<AlgoExecutionAuditDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    algoService.admin
      .listAll()
      .then((res) => setAlgos(res.items))
      .catch(() => setAlgos([]));
    userService
      .list({ page: 1, page_size: 200 })
      .then((res) => setUsers(res.items))
      .catch(() => setUsers([]));
  }, []);

  // If "Failures only" is toggled on while a specific TRADE_EXECUTED status
  // filter is selected, those two filters would contradict each other and
  // silently return zero rows -- clear the status filter instead.
  useEffect(() => {
    if (failuresOnly && status === "TRADE_EXECUTED") setStatus("");
  }, [failuresOnly, status]);

  const runSearch = useCallback(
    async (nextOffset: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await algoExecutionAuditService.list({
          algo_id: algoId || undefined,
          user_id: userId || undefined,
          status: status || undefined,
          since: toIsoOrUndefined(since),
          until: toIsoOrUndefined(until),
          search: search || undefined,
          failed_stage: failedStage || undefined,
          exclude_executed: failuresOnly || undefined,
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
        setItems(res.items);
        setTotal(res.total);
        setOffset(nextOffset);
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [algoId, userId, status, since, until, search, failedStage, failuresOnly]
  );

  useEffect(() => {
    runSearch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brokerNames = Array.from(
    new Set(items.map((i) => i.broker_name).filter((b): b is string => !!b))
  );
  const visibleItems = brokerFilter ? items.filter((i) => i.broker_name === brokerFilter) : items;

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const data = await algoExecutionAuditService.getDetail(id);
      setDetail(data);
    } catch (err) {
      setDetailError(extractErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedId((cur) => (cur === id ? null : id));
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Algo Execution Monitor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Why did (or didn't) a trade happen? Every algo/user-strategy evaluation, with a 13-stage execution pipeline
        trace, conditions, decision, and any broker/API/system failure.
      </Typography>

      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            select
            label="Algo"
            size="small"
            value={algoId}
            onChange={(e) => setAlgoId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All algos</MenuItem>
            {algos.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.algo_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="User"
            size="small"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All users</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.full_name} ({u.email})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Broker"
            size="small"
            value={brokerFilter}
            onChange={(e) => setBrokerFilter(e.target.value)}
            sx={{ minWidth: 160 }}
            disabled={brokerNames.length === 0}
          >
            <MenuItem value="">All brokers</MenuItem>
            {brokerNames.map((b) => (
              <MenuItem key={b} value={b}>
                {b}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Status"
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {ALL_STATUSES.map((s) => (
              <MenuItem key={s} value={s} disabled={failuresOnly && s === "TRADE_EXECUTED"}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Failed Stage"
            size="small"
            value={failedStage}
            onChange={(e) => setFailedStage(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Any stage</MenuItem>
            {STAGE_ORDER.map((key) => (
              <MenuItem key={key} value={key}>
                {STAGE_LABELS[key]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Since"
            type="datetime-local"
            size="small"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Until"
            type="datetime-local"
            size="small"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Search"
            size="small"
            placeholder="Summary, name, or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />

          <FormControlLabel
            control={
              <Switch checked={failuresOnly} onChange={(e) => setFailuresOnly(e.target.checked)} size="small" />
            }
            label="Failures only"
          />

          <Button variant="contained" onClick={() => runSearch(0)} disabled={loading}>
            {loading ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
        </Stack>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Time</TableCell>
                <TableCell>Algo</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Execution Pipeline</TableCell>
                <TableCell>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((row) => (
                <Fragment key={row.id}>
                  <TableRow hover sx={{ cursor: "pointer" }} onClick={() => toggleExpanded(row.id)}>
                    <TableCell>
                      <IconButton size="small">
                        {expandedId === row.id ? (
                          <KeyboardArrowDownIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowRightIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{new Date(row.candle_time).toLocaleString()}</TableCell>
                    <TableCell>{row.algo_name}</TableCell>
                    <TableCell>{row.user_full_name ?? "—"}</TableCell>
                    <TableCell>{row.broker_name ?? "—"}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <TerminalFailureTag status={row.status} terminalFailureStage={row.terminal_failure_stage} />
                        <StatusChip status={row.status} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <PipelineStepper stages={row.pipeline_stages} />
                    </TableCell>
                    <TableCell
                      sx={{ maxWidth: 340, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {row.summary}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ p: 0 }}>
                      <Collapse in={expandedId === row.id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: "action.hover" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              13-Stage Execution Pipeline
                            </Typography>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(row.id);
                              }}
                            >
                              View full detail
                            </Button>
                          </Stack>
                          <PipelineStageTable stages={row.pipeline_stages} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
              {!loading && visibleItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      No records match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {total} total record{total === 1 ? "" : "s"} — page {page} of {totalPages}
        </Typography>
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            disabled={offset === 0 || loading}
            onClick={() => runSearch(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            size="small"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => runSearch(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </Stack>
      </Stack>
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Execution Audit Detail
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {detail && !detailLoading && (
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {detail.algo_name}
                    {detail.strategy_key && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({detail.strategy_key})
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(detail.candle_time).toLocaleString()}
                    {detail.user_full_name && ` — ${detail.user_full_name} (${detail.user_email})`}
                    {detail.broker_name &&
                      ` — ${detail.broker_name}${detail.broker_account_label ? ` / ${detail.broker_account_label}` : ""}`}
                  </Typography>
                </Box>
                <Stack spacing={0.5} alignItems="flex-end">
                  <TerminalFailureTag status={detail.status} terminalFailureStage={detail.terminal_failure_stage} />
                  <StatusChip status={detail.status} />
                </Stack>
              </Stack>

              <Typography variant="body1">{detail.summary}</Typography>

              {detail.error_detail && (
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Error detail
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", m: 0 }}
                  >
                    {detail.error_detail}
                  </Typography>
                </Paper>
              )}

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  13-Stage Execution Pipeline
                </Typography>
                <PipelineStepper stages={detail.pipeline_stages} dotSize={16} />
                <Box sx={{ mt: 1.5 }}>
                  <PipelineStageTable stages={detail.pipeline_stages} />
                </Box>
              </Box>

              {detail.conditions.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Conditions
                  </Typography>
                  <Stack spacing={0.75}>
                    {detail.conditions.map((c, idx) => (
                      <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                        {c.passed ? (
                          <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                        ) : (
                          <CancelIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {c.label}
                          </Typography>
                          {c.detail && (
                            <Typography variant="body2" color="text.secondary">
                              {c.detail}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {detail.resolved_contracts.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Resolved Contract{detail.resolved_contracts.length > 1 ? "s" : ""}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Leg</TableCell>
                          <TableCell>Strike</TableCell>
                          <TableCell>Expiry</TableCell>
                          <TableCell>LTP</TableCell>
                          <TableCell>Liquidity Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.resolved_contracts.map((rc, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{rc.leg_role ?? "—"}</TableCell>
                            <TableCell>{rc.strike ?? "—"}</TableCell>
                            <TableCell>{rc.expiry_date ?? "—"}</TableCell>
                            <TableCell>{rc.ltp ?? "—"}</TableCell>
                            <TableCell>{rc.liquidity_score ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {detail.positions.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Position{detail.positions.length > 1 ? "s" : ""}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Leg</TableCell>
                          <TableCell>Symbol</TableCell>
                          <TableCell>Strike</TableCell>
                          <TableCell>Qty</TableCell>
                          <TableCell>Entry</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Exit</TableCell>
                          <TableCell>P&amp;L</TableCell>
                          <TableCell>Broker Order</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.positions.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>{p.leg_role ?? "—"}</TableCell>
                            <TableCell>
                              {p.symbol_code} {p.option_type ?? ""}
                            </TableCell>
                            <TableCell>{p.strike ?? "—"}</TableCell>
                            <TableCell>{p.quantity}</TableCell>
                            <TableCell>{p.entry_price ?? "—"}</TableCell>
                            <TableCell>
                              <Chip label={p.status} size="small" color={p.status === "OPEN" ? "success" : "default"} />
                            </TableCell>
                            <TableCell>
                              {p.exit_price ?? "—"} {p.exit_reason ? `(${p.exit_reason})` : ""}
                            </TableCell>
                            <TableCell>{p.realized_pnl ?? "—"}</TableCell>
                            <TableCell>{p.broker_order_id ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AlgoExecutionMonitorPage;
